import { useState, useEffect } from 'react';
import axios from 'axios';
import clarity from '@microsoft/clarity';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2, TrendingUp, Smartphone, Search, Code, Zap, Rocket, ChevronRight, ChevronDown, Star, ArrowRight, Monitor, Clock, Sparkles, Target, Shield, ExternalLink, Trophy, Mail, Bell, Utensils, Car, Truck, Stethoscope, BarChart3, Download, FileText, AlertTriangle, Eye, Mic, Bot, DollarSign, GraduationCap, X, CheckCircle, Menu, AlertCircle, MapPin } from 'lucide-react';
import AnalyzerWizard from '@/components/AnalyzerWizard';
import AnalyzerDiscoveryFeed from '@/components/AnalyzerDiscoveryFeed';
import SEO from '@/components/SEO';
import { ResponsiveDesignAnalysis, SEOTechnicalAnalysis, PriorityFixes } from '@/components/SEOAnalysisComponents';
import PortfolioShowcase from '@/components/PortfolioShowcase';
import useCardScanTracking from '@/hooks/useCardScanTracking';

const BANNER_HEADLINES = [
  { title: "Is Your Business Invisible to ChatGPT? Let's Fix That.", sub: "AI visibility check shows exactly why AI chatbots aren't recommending you — and how to be the answer customers get next time." },
  { title: "AI Is Eating Google Search. Is Your Business On The Menu?", sub: "Free 30-second AI visibility analysis — see how ChatGPT, Google AI Overviews, and Perplexity view your business right now." },
  { title: "Your Competitors Are Showing Up in ChatGPT. Are You?", sub: "We build websites, optimize content, and structure data so AI actually recommends you to searchers." },
  { title: "The #1 Reason You're Losing Customers in 2026: AI Doesn't Know You Exist.", sub: "Let our team fix that. Custom websites, AI-optimized content, and local SEO that gets you into AI search results." },
  { title: "Small Business Marketing Powered by AI — Done For You.", sub: "Website design, AI search optimization, automated social posting, and local SEO — one team, one monthly fee." },
  { title: "Stop Being the Best-Kept Secret in Your City.", sub: "When customers ask AI for the best [your service] in [your city], we make sure your business is the answer." },
  { title: "Your Website From 5 Years Ago Isn't Built for AI Search.", sub: "We rebuild it with structured data, schema markup, and local citations — so ChatGPT, Bing, and Google actually recommend you." },
  { title: "One Monthly Plan. Website + AI SEO + Social Posting. Done.", sub: "Stop juggling agencies. Everything you need to dominate local + AI search, handled by one team." },
];
import ProfessionalHero from '@/components/ProfessionalHero';
import AIVisibilityTeaser from '@/components/AIVisibilityTeaser';
import SEOIntelligenceShowcase from '@/components/seo/SEOIntelligenceShowcase';
import AlwaysOnMarketingTeam from '@/components/seo/AlwaysOnMarketingTeam';
import { BACKEND_URL } from '../utils/api';
import homepageFaqs from '../data/homepageFaqs.json';

const API = `${BACKEND_URL}/api`;

function HomePage() {
  const { t, i18n } = useTranslation(['home']);
  const lang = (i18n.language || 'en').split('-')[0] === 'es' ? 'es' : 'en';
  useCardScanTracking();
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [bannerHeadline, setBannerHeadline] = useState(null);
  const [bannerIndex, setBannerIndex] = useState(0);

  // Session id for A/B attribution — stable across refreshes within a browsing session
  const [abSessionId] = useState(() => {
    let sid = sessionStorage.getItem('hp_ab_session');
    if (!sid) {
      sid = 'hp-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('hp_ab_session', sid);
    }
    return sid;
  });

  // Pick + log headline variant once per page load
  useEffect(() => {
    const idx = Math.floor(Math.random() * BANNER_HEADLINES.length);
    const hl = BANNER_HEADLINES[idx];
    setBannerHeadline(hl);
    setBannerIndex(idx);
    sessionStorage.setItem('hp_ab_variant', String(idx));
    // Fire impression (server dedups within a session)
    axios.post(`${BACKEND_URL}/api/homepage-ab/impression`, {
      variant_index: idx,
      headline: hl.title,
      session_id: abSessionId,
    }).catch(() => {});
  }, []); // eslint-disable-line

  const [contactForm, setContactForm] = useState({ 
    name: '', 
    position: '',
    email: '', 
    phone: '',
    company: '', 
    preferredContact: 'email',
    message: '',
    company_url: ''  // honeypot — must stay empty; bots fill it
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [useWizard, setUseWizard] = useState(true); // Toggle for new wizard
  
  // Lead Capture States
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [pendingAnalysisResult, setPendingAnalysisResult] = useState(null);
  const [selectedReportTier, setSelectedReportTier] = useState(null);
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    businessUrl: '',
    role: '',
    relationship: '',
    preferredContactMethod: 'email',
    allowContact: true,
    agreedToContact: false,
    competitorUrl: '' // For comparative analysis
  });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  
  // Enhanced personalization fields
  const [siteType, setSiteType] = useState('');
  const [goals, setGoals] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({
    sellsProducts: '',
    trafficDrops: '',
    cms: '',
    monthlyVisitors: ''
  });
  const [analyzingProgress, setAnalyzingProgress] = useState(0);
  const [analyzingMessage, setAnalyzingMessage] = useState('');
  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState({ limit: 50, window: '24 hours' });
  
  // PDF generation state


  // Initialize Microsoft Clarity Heat Mapping
  useEffect(() => {
    const clarityProjectId = process.env.REACT_APP_CLARITY_PROJECT_ID;
    
    if (clarityProjectId && clarityProjectId.trim() !== '') {
      try {
        clarity.init(clarityProjectId);
        console.log('✅ Microsoft Clarity initialized for heat mapping');
      } catch (error) {
        console.error('Clarity initialization error:', error);
      }
    } else {
      console.log('ℹ️ Clarity Project ID not configured. Add REACT_APP_CLARITY_PROJECT_ID to .env');
    }
  }, []);

  // Expandable sections state
  const [expandedSections, setExpandedSections] = useState({
    performance: false,
    security: false,
    seo: false,
    mobile: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const siteTypes = [
    'eCommerce Store',
    'Restaurant / Food Service',
    'Dental Practice',
    'Medical Practice / Healthcare',
    'Car Dealership',
    'RV Dealership',
    'Auto Repair / Service',
    'Cleaning Service',
    'Law Firm',
    'Real Estate',
    'Contractor / Home Services',
    'Salon / Spa',
    'Gym / Fitness',
    'Blog/Content Site',
    'SaaS Platform',
    'Non-Profit',
    'Other'
  ];

  const goalOptions = [
    'Boost Conversions',
    'Improve SEO Rankings',
    'Increase Page Speed',
    'Better Mobile Experience',
    'Reduce Bounce Rate',
    'Fix Technical Issues'
  ];

  // Dynamic questions based on site type
  const getTypeSpecificQuestions = (type) => {
    const questions = {
      'eCommerce Store': [
        { id: 'hasCart', label: 'Do you have shopping cart issues or high abandonment?', type: 'radio', options: ['Yes - Major Issue', 'Yes - Minor Issue', 'No Issues', 'Not Sure'] },
        { id: 'paymentMethods', label: 'Which payment methods do you accept?', type: 'checkbox', options: ['Credit Cards', 'PayPal', 'Apple Pay', 'Google Pay', 'Crypto', 'Other'] },
        { id: 'avgOrderValue', label: 'Average order value?', type: 'radio', options: ['Under $50', '$50-$100', '$100-$250', '$250-$500', 'Over $500'] },
        { id: 'productCount', label: 'How many products?', type: 'radio', options: ['1-10', '11-50', '51-200', '200-1000', '1000+'] }
      ],
      'Restaurant / Food Service': [
        { id: 'hasOnlineOrdering', label: 'Do you offer online ordering?', type: 'radio', options: ['Yes - Own System', 'Yes - Third Party (UberEats, etc.)', 'No', 'Planning To'] },
        { id: 'reservationSystem', label: 'Do you take online reservations?', type: 'radio', options: ['Yes', 'No', 'Walk-ins Only'] },
        { id: 'menuUpdates', label: 'How often do you update your menu online?', type: 'radio', options: ['Weekly', 'Monthly', 'Quarterly', 'Rarely', 'Never'] },
        { id: 'deliveryRange', label: 'Delivery service?', type: 'radio', options: ['Yes - Own Drivers', 'Yes - Third Party', 'No Delivery', 'Pickup Only'] }
      ],
      'Dental Practice': [
        { id: 'appointmentBooking', label: 'Can patients book appointments online?', type: 'radio', options: ['Yes', 'No - Phone Only', 'Planning To Add'] },
        { id: 'patientPortal', label: 'Do you have a patient portal?', type: 'radio', options: ['Yes', 'No', 'In Development'] },
        { id: 'insuranceInfo', label: 'Is insurance information clear on your site?', type: 'radio', options: ['Very Clear', 'Somewhat Clear', 'Not Clear', 'Not Listed'] },
        { id: 'newPatients', label: 'Are you accepting new patients?', type: 'radio', options: ['Yes - Actively Seeking', 'Yes - Limited Capacity', 'Waitlist Only', 'No'] }
      ],
      'Medical Practice / Healthcare': [
        { id: 'appointmentSystem', label: 'Online appointment scheduling?', type: 'radio', options: ['Yes - Integrated', 'Yes - Third Party', 'No - Phone Only'] },
        { id: 'telehealth', label: 'Do you offer telehealth/virtual visits?', type: 'radio', options: ['Yes', 'No', 'Planning To'] },
        { id: 'hipaaCompliant', label: 'Is your website HIPAA compliant?', type: 'radio', options: ['Yes - Verified', 'I Think So', 'Not Sure', 'No'] },
        { id: 'patientReviews', label: 'Do you display patient reviews/testimonials?', type: 'radio', options: ['Yes - Many', 'Yes - A Few', 'No'] }
      ],
      'Car Dealership': [
        { id: 'inventoryOnline', label: 'Is your full inventory online with photos?', type: 'radio', options: ['Yes - Always Updated', 'Yes - Sometimes Outdated', 'Partial Inventory', 'No'] },
        { id: 'virtualTours', label: 'Do you offer virtual tours or 360° views?', type: 'radio', options: ['Yes', 'No', 'Planning To'] },
        { id: 'financingCalc', label: 'Do you have a financing calculator?', type: 'radio', options: ['Yes', 'No', 'Links to Lender'] },
        { id: 'leadCapture', label: 'How do customers inquire about vehicles?', type: 'checkbox', options: ['Contact Form', 'Live Chat', 'Phone', 'Text', 'Email'] }
      ],
      'RV Dealership': [
        { id: 'rvInventory', label: 'Full RV inventory online with specs?', type: 'radio', options: ['Yes - Complete', 'Yes - Partial', 'Limited Photos', 'No'] },
        { id: 'floorPlans', label: 'Do you show RV floor plans?', type: 'radio', options: ['Yes - Interactive', 'Yes - PDF', 'No'] },
        { id: 'tradeIns', label: 'Can customers get trade-in values online?', type: 'radio', options: ['Yes', 'No', 'Planning To'] },
        { id: 'rvType', label: 'What types do you specialize in?', type: 'checkbox', options: ['Class A', 'Class B', 'Class C', 'Fifth Wheel', 'Travel Trailer', 'Toy Hauler'] }
      ],
      'Auto Repair / Service': [
        { id: 'onlineBooking', label: 'Can customers book service appointments online?', type: 'radio', options: ['Yes', 'No - Phone Only', 'Planning To'] },
        { id: 'serviceEstimates', label: 'Do you provide online estimates?', type: 'radio', options: ['Yes - Instant', 'Yes - Manual Quote', 'No'] },
        { id: 'serviceCoupons', label: 'Do you offer online coupons/specials?', type: 'radio', options: ['Yes', 'No', 'Sometimes'] },
        { id: 'repairUpdates', label: 'How do you update customers on repairs?', type: 'checkbox', options: ['Phone', 'Text/SMS', 'Email', 'App', 'Portal'] }
      ],
      'Cleaning Service': [
        { id: 'instantQuotes', label: 'Can customers get instant quotes online?', type: 'radio', options: ['Yes', 'No - Must Call', 'Form Submission'] },
        { id: 'recurringBooking', label: 'Can customers book recurring services online?', type: 'radio', options: ['Yes', 'No', 'Planning To'] },
        { id: 'serviceTypes', label: 'What services do you offer?', type: 'checkbox', options: ['Residential', 'Commercial', 'Deep Clean', 'Move Out/In', 'Post-Construction', 'Carpet'] },
        { id: 'serviceArea', label: 'Is your service area clearly defined?', type: 'radio', options: ['Yes - Very Clear', 'Somewhat Clear', 'Not Clear'] }
      ],
      'Law Firm': [
        { id: 'practiceAreas', label: 'Are your practice areas clearly organized?', type: 'radio', options: ['Very Clear', 'Somewhat Clear', 'Confusing'] },
        { id: 'consultationBooking', label: 'Can clients book consultations online?', type: 'radio', options: ['Yes', 'No - Phone/Email Only', 'Contact Form'] },
        { id: 'caseResults', label: 'Do you showcase case results/testimonials?', type: 'radio', options: ['Yes - Many', 'Yes - Some', 'No'] },
        { id: 'lawyerBios', label: 'Are attorney bios comprehensive?', type: 'radio', options: ['Yes - Detailed', 'Basic Info Only', 'Limited'] }
      ],
      'Contractor / Home Services': [
        { id: 'projectGallery', label: 'Do you have a project photo gallery?', type: 'radio', options: ['Yes - Extensive', 'Yes - Limited', 'No'] },
        { id: 'quoteRequest', label: 'How do customers request quotes?', type: 'checkbox', options: ['Online Form', 'Phone', 'Email', 'In-Person Only'] },
        { id: 'serviceAreas', label: 'Is your service area clearly defined?', type: 'radio', options: ['Yes', 'No', 'Sort Of'] },
        { id: 'licensing', label: 'Are licenses/certifications displayed?', type: 'radio', options: ['Yes - Prominently', 'Yes - Small Print', 'No'] }
      ],
      'Real Estate': [
        { id: 'propertySearch', label: 'Do you have an integrated property search?', type: 'radio', options: ['Yes - MLS Connected', 'Yes - Manual Updates', 'Links to MLS', 'No'] },
        { id: 'virtualTours', label: 'Do you offer virtual property tours?', type: 'radio', options: ['Yes - Most Listings', 'Yes - Some', 'No'] },
        { id: 'mortgageCalc', label: 'Do you have a mortgage calculator?', type: 'radio', options: ['Yes', 'No', 'Links to Lender'] },
        { id: 'agentProfiles', label: 'Are agent profiles detailed with sold properties?', type: 'radio', options: ['Yes', 'Basic Info Only', 'No'] }
      ]
    };

    return questions[type] || [];
  };

  const funFacts = [
    "Pulling down your homepage — checking how real visitors experience it.",
    "Reading every tag, script, and image — nothing gets skipped.",
    "Measuring Core Web Vitals — LCP, CLS, INP. The stuff Google grades you on.",
    "Auditing SEO signals — titles, schema, internal links, freshness.",
    "Checking AI search visibility — is ChatGPT citing you? Gemini? Perplexity?",
    "Testing mobile responsiveness — 70% of your local traffic is on phones.",
    "Looking for accessibility wins — good for users, good for Google.",
    "Running our proprietary local-rank benchmark — comparing you to 3 competitors.",
    "Prioritizing fixes by impact vs effort — the 30-minute wins surface first.",
    "Wrapping up your report — the good, the ugly, the obvious wins."
  ];

  const handleGoalToggle = (goal) => {
    setGoals(prev => 
      prev.includes(goal) 
        ? prev.filter(g => g !== goal)
        : [...prev, goal]
    );
  };

  const handleWizardComplete = (wizardData) => {
    // Map wizard data to existing state
    setWebsiteUrl(wizardData.url);
    setSiteType(wizardData.siteType || '');
    setGoals(wizardData.objectives || []);
    
    // Run analysis immediately - lead capture comes AFTER analysis
    handleAnalyzeWithData(wizardData);
  };

  const [emailVerifyState, setEmailVerifyState] = useState(null);  // {ok, reason, message, suggestion}
  const [emailChecking, setEmailChecking] = useState(false);

  const verifyLeadEmail = async (email) => {
    if (!email) return null;
    setEmailChecking(true);
    try {
      const r = await axios.post(`${API}/verify-email`, { email }, { timeout: 8000 });
      setEmailVerifyState(r.data);
      return r.data;
    } catch {
      const fallback = { ok: true, email, suggestion: null };
      setEmailVerifyState(fallback);
      return fallback;
    } finally {
      setEmailChecking(false);
    }
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();

    if (!leadForm.email) {
      alert('Please provide your email address to unlock your full report.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(leadForm.email)) {
      alert('Please enter a valid email address.');
      return;
    }

    // Tier-1 MX + typo check
    const verdict = await verifyLeadEmail(leadForm.email);
    if (verdict && verdict.ok === false) {
      // Surface the inline error (handled by render) and halt submit
      return;
    }
    
    setLeadSubmitting(true);
    
    try {
      const leadData = {
        email: leadForm.email,
        phone: leadForm.phone || '',
        name: leadForm.name?.trim() || leadForm.email.split('@')[0],
        website_analyzed: pendingAnalysisResult?.url || websiteUrl,
        analysis_score: pendingAnalysisResult?.overall_score,
        critical_issues_count: pendingAnalysisResult?.critical_issues?.length || 0,
        source: 'website_analyzer',
        analysis_data: pendingAnalysisResult || null,
      };
      
      const response = await axios.post(`${API}/analysis-leads`, leadData);
      
      if (!response.data?.success) {
        console.error('Lead save returned unsuccessful:', response.data);
      }
    } catch (error) {
      console.error('Error saving lead:', error);
      // Non-blocking: log the error but still show results
    }
    
    // Always show results after lead capture attempt
    const resultToShow = { ...pendingAnalysisResult, objectives: (pendingAnalysisResult && pendingAnalysisResult.objectives) || goals || [] };
    setAnalysisResult(resultToShow);
    try { sessionStorage.setItem('wts_analysis_result', JSON.stringify(resultToShow)); } catch (e) { /* ignore */ }
    setShowLeadCapture(false);
    setPendingAnalysisResult(null);
    setSelectedReportTier(null);
    setLeadSubmitting(false);

    // A/B conversion attribution — this session ran an analysis
    try {
      const ab_variant = sessionStorage.getItem('hp_ab_variant');
      if (ab_variant !== null) {
        axios.post(`${BACKEND_URL}/api/homepage-ab/conversion`, {
          variant_index: parseInt(ab_variant, 10),
          session_id: abSessionId,
          url_analyzed: pendingAnalysisResult?.url || '',
        }).catch(() => {});
      }
    } catch { /* no-op */ }
    
    setTimeout(() => {
      document.getElementById('analysis-results')?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const handleAnalyzeWithData = async (wizardData) => {
    setAnalyzing(true);
    setAnalysisResult(null);
    setAnalyzingProgress(0);
    
    // Scroll to analyzer section to show progress
    setTimeout(() => {
      document.getElementById('analyzer')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    
    // Simulate progress with fun facts - faster updates for more visible movement
    const progressInterval = setInterval(() => {
      setAnalyzingProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        // Smaller increments but faster interval = smoother animation
        const increment = 2 + Math.random() * 5;
        const newProgress = prev + increment;
        const factIndex = Math.floor((newProgress / 100) * funFacts.length);
        setAnalyzingMessage(funFacts[Math.min(factIndex, funFacts.length - 1)]);
        return Math.min(newProgress, 90);
      });
    }, 300); // Faster interval for smoother progress
    
    try {
      // Build comprehensive payload from wizard data
      const payload = {
        url: wizardData.url,
        
        // Basic info
        website_type: wizardData.websiteType,
        site_type: wizardData.siteType,
        site_type_other: wizardData.siteTypeOther, // Custom input for "Other"
        industry: wizardData.industry,
        objectives: wizardData.objectives,
        challenges: wizardData.challenges,
        
        // GA4 & Analytics
        has_ga4: wizardData.hasGA4,
        ga4_linked_to_gsc: wizardData.ga4LinkedToGSC,
        tracks_conversions: wizardData.tracksConversions,
        
        // Performance metrics
        monthly_organic_sessions: wizardData.monthlyOrganicSessions,
        bounce_rate: wizardData.bounceRate,
        avg_engagement_time: wizardData.avgEngagementTime,
        has_enhanced_measurement: wizardData.hasEnhancedMeasurement,
        
        // Business-specific
        business_model: wizardData.businessModel,
        target_audience: wizardData.targetAudience,
        monthly_traffic_goal: wizardData.monthlyTrafficGoal,
        monthly_revenue_goal: wizardData.monthlyRevenueGoal,
        key_competitors: wizardData.keyCompetitors,
        current_tools: wizardData.currentTools,
        marketing_channels: wizardData.marketingChannels,
        budget_range: wizardData.budgetRange,
        timeline: wizardData.timeline,
        
        // Personal-specific
        personal_purpose: wizardData.personalPurpose,
        target_audience_size: wizardData.targetAudienceSize,
        content_update_frequency: wizardData.contentUpdateFrequency,
        
        // Car/RV Dealer specific
        inventory_feeds: wizardData.inventoryFeeds,
        inventory_feeds_other: wizardData.inventoryFeedsOther,
        has_service_scheduling: wizardData.hasServiceScheduling,
        service_dept_features: wizardData.serviceDeptFeatures,
        inventory_size: wizardData.inventorySize,
        number_of_locations: wizardData.numberOfLocations,
        dealership_type: wizardData.dealershipType,
        
        // Legacy support
        goals: wizardData.goals || wizardData.objectives
      };
      
      const response = await axios.post(`${API}/analyze`, payload);
      
      // Store result and show lead capture form instead of showing results directly
      setPendingAnalysisResult(response.data);
      setAnalyzingProgress(100);
      setAnalyzingMessage('Analysis complete!');
      clearInterval(progressInterval);
      setAnalyzing(false);
      
      // Show lead capture modal
      setShowLeadCapture(true);
    } catch (error) {
      console.error('Analysis error:', error);
      clearInterval(progressInterval);
      
      // Handle rate limit error specifically
      if (error.response?.status === 429) {
        const detail = error.response?.data?.detail;
        if (typeof detail === 'object' && detail.error === 'rate_limit_exceeded') {
          setRateLimitInfo({ 
            limit: detail.limit || 50, 
            window: detail.window || '24 hours',
            message: detail.message 
          });
        }
        setShowRateLimitModal(true);
      } else {
        alert('Failed to analyze website. Please try again or contact us if the issue persists.');
      }
    } finally {
      setTimeout(() => {
        setAnalyzing(false);
        setAnalyzingProgress(0);
      }, 500);
    }
  };

  const handleAnalyze = async (url) => {
    // Use passed URL or fall back to state
    const urlToAnalyze = url || websiteUrl;
    if (!urlToAnalyze) return;
    
    // Set the websiteUrl state if passed from hero
    if (url) {
      setWebsiteUrl(url);
      setLeadForm(prev => ({ ...prev, businessUrl: url }));
    }
    
    // Run analysis directly - no pre-qualification gate
    setAnalyzing(true);
    setAnalysisResult(null);
    setAnalyzingProgress(0);
    
    const progressInterval = setInterval(() => {
      setAnalyzingProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        const increment = 2 + Math.random() * 5;
        const newProgress = prev + increment;
        const factIndex = Math.floor((newProgress / 100) * funFacts.length);
        setAnalyzingMessage(funFacts[Math.min(factIndex, funFacts.length - 1)]);
        return Math.min(newProgress, 90);
      });
    }, 300);
    
    try {
      const payload = {
        url: urlToAnalyze,
        site_type: siteType,
        goals: goals,
        quiz_answers: showAdvanced ? quizAnswers : null,
        key_competitors: leadForm.competitorUrl || null
      };
      
      const response = await axios.post(`${API}/analyze`, payload);
      
      // Store result and show lead capture gate
      setPendingAnalysisResult(response.data);
      setAnalyzingProgress(100);
      setAnalyzingMessage('Analysis complete!');
      clearInterval(progressInterval);
      setAnalyzing(false);
      
      // Show lead capture modal with teaser
      setShowLeadCapture(true);
    } catch (error) {
      console.error('Analysis error:', error);
      clearInterval(progressInterval);
      
      if (error.response?.status === 429) {
        const detail = error.response?.data?.detail;
        if (typeof detail === 'object' && detail.error === 'rate_limit_exceeded') {
          setRateLimitInfo({ 
            limit: detail.limit || 50, 
            window: detail.window || '24 hours',
            message: detail.message 
          });
        }
        setShowRateLimitModal(true);
      } else {
        alert('Failed to analyze website. Please try again or contact us if the issue persists.');
      }
    } finally {
      setTimeout(() => {
        setAnalyzing(false);
        setAnalyzingProgress(0);
      }, 500);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, contactForm);
      setContactSuccess(true);
      setContactForm({ name: '', email: '', company: '', message: '', company_url: '' });
      setTimeout(() => setContactSuccess(false), 5000);
    } catch (error) {
      console.error('Contact error:', error);
      alert('Failed to submit contact form. Please try again.');
    } finally {
      setContactSubmitting(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <>
      <SEO 
        title="Web-TechServices | #1 Nationwide Web Design & SEO Agency | 50 States"
        description="Premier AI-powered web design, SEO, and digital marketing agency serving all 50 US states. Custom websites, local SEO, eCommerce solutions. Free website analysis. Headquartered in DFW, serving nationwide since 2000."
        keywords="web design, SEO agency, digital marketing, nationwide web development, local SEO, AI web development, website design company, ecommerce development, web design near me"
        canonical="https://web-techservices.com/"
        ogImage="https://static.prod-images.emergentagent.com/jobs/3ffa77a1-0bda-46eb-9179-0a59cf5bf2e9/images/6a98610b6231c606fb2427cf455bc688e724a3a8c86b57edf4b5d30439b13924.png"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://web-techservices.com/#organization",
              "name": "Web-TechServices",
              "url": "https://web-techservices.com",
              "logo": {
                "@type": "ImageObject",
                "url": "https://web-techservices.com/logo.png",
                "width": 200,
                "height": 60
              },
              "image": "https://web-techservices.com/og-contest-new.png",
              "description": "Premier AI-powered web design, SEO, and digital marketing agency serving businesses nationwide. Headquartered in Dallas-Fort Worth, Texas since 2000.",
              "foundingDate": "2000",
              "areaServed": [
                { "@type": "Country", "name": "United States" },
                { "@type": "City", "name": "Dallas", "containedInPlace": { "@type": "State", "name": "Texas" } },
                { "@type": "City", "name": "Fort Worth", "containedInPlace": { "@type": "State", "name": "Texas" } },
                { "@type": "City", "name": "Los Angeles", "containedInPlace": { "@type": "State", "name": "California" } },
                { "@type": "City", "name": "New York", "containedInPlace": { "@type": "State", "name": "New York" } },
                { "@type": "City", "name": "Chicago", "containedInPlace": { "@type": "State", "name": "Illinois" } }
              ],
              "sameAs": [
                "https://www.facebook.com/webtechservices",
                "https://www.linkedin.com/company/web-techservices",
                "https://twitter.com/webtechservices"
              ],
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Dallas",
                "addressRegion": "TX",
                "postalCode": "75201",
                "addressCountry": "US"
              }
            },
            {
              "@type": "WebSite",
              "@id": "https://web-techservices.com/#website",
              "url": "https://web-techservices.com",
              "name": "Web-TechServices",
              "description": "AI-Powered Web Design & SEO Agency",
              "publisher": { "@id": "https://web-techservices.com/#organization" }
            },
            {
              "@type": "WebPage",
              "@id": "https://web-techservices.com/#webpage",
              "url": "https://web-techservices.com",
              "name": "Web-TechServices | #1 Nationwide Web Design & SEO Agency",
              "isPartOf": { "@id": "https://web-techservices.com/#website" },
              "about": { "@id": "https://web-techservices.com/#organization" },
              "description": "Premier AI-powered web design, SEO, and digital marketing agency serving all 50 US states. Headquartered in Dallas-Fort Worth."
            },
            {
              "@type": "ProfessionalService",
              "@id": "https://web-techservices.com/#service",
              "name": "Web-TechServices",
              "image": "https://web-techservices.com/og-contest-new.png",
              "priceRange": "$$",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Dallas",
                "addressRegion": "TX",
                "addressCountry": "US"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": 32.7767,
                "longitude": -96.7970
              },
              "openingHoursSpecification": [
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                  "opens": "09:00",
                  "closes": "18:00"
                }
              ],
              "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": "Web Services",
                "itemListElement": [
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Web Design & Development" } },
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "SEO Services" } },
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Digital Marketing" } },
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "eCommerce Development" } },
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "AI Web Solutions" } }
                ]
              },
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.9",
                "reviewCount": "127",
                "bestRating": "5",
                "worstRating": "1"
              }
            }
          ]
        }}
      />
      
      {/* Gift Portfolio Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="hidden sm:inline-flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-full">
                <GraduationCap className="w-6 h-6 text-blue-400" />
              </span>
              <div className="text-center sm:text-left">
                <p className="font-bold text-base sm:text-lg">
                  {t('home:banners.giftPrefix')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">{t('home:banners.giftAccent')}</span> {t('home:banners.giftSuffix')}
                </p>
                <p className="text-sm text-blue-200 mt-1">
                  {t('home:banners.giftSub1')} <strong className="text-white">TheirNameMD.com</strong>. {t('home:banners.giftSub2')}
                </p>
              </div>
            </div>
            <Link 
              to="/gift-portfolio" 
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-bold text-sm transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              {t('home:banners.giftCta')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
      
      {/* New Business Offer Banner */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="hidden sm:inline-flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-full">
                <Sparkles className="w-6 h-6 text-blue-400" />
              </span>
              <div className="text-center sm:text-left">
                <p className="font-bold text-base sm:text-lg text-white">
                  {t('home:banners.newBizTitle')}
                </p>
                <p className="text-sm text-slate-300 mt-1">
                  {t('home:banners.newBizSub1')} <strong className="text-white">{t('home:banners.newBizSub2')}</strong> {t('home:banners.newBizSub3')}
                </p>
              </div>
            </div>
            <a 
              href="/new-business" 
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-bold text-sm transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              {t('home:banners.newBizCta')}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Announcement Banner - Compact (contest replaced with on-brand CTA) */}
      {bannerHeadline && (
      <section data-testid="homepage-announcement-banner" className="bg-gradient-to-r from-slate-900 to-slate-800 py-5 px-4">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-white">
            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="font-semibold text-sm sm:text-base">{lang === 'es' ? (t(`home:bannerHeadlines.${bannerIndex}.title`, { defaultValue: bannerHeadline.title })) : bannerHeadline.title}</p>
              <p className="text-slate-400 text-xs sm:text-sm">{lang === 'es' ? (t(`home:bannerHeadlines.${bannerIndex}.sub`, { defaultValue: bannerHeadline.sub })) : bannerHeadline.sub}</p>
            </div>
          </div>
          <a
            href="#hero-analyzer"
            data-testid="homepage-announcement-cta"
            onClick={() => {
              axios.post(`${BACKEND_URL}/api/homepage-ab/click`, {
                variant_index: bannerIndex,
                session_id: abSessionId,
              }).catch(() => {});
            }}
          >
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold whitespace-nowrap">
              {t('home:banners.aiCheckCta')}
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </a>
        </div>
      </section>
      )}
      
      {/* Professional Hero Section with Website Analyzer */}
      <ProfessionalHero onAnalyze={handleAnalyze} analyzing={analyzing} />

      {/* AI Visibility Teaser - front and center */}
      <AIVisibilityTeaser />

      {/* Social Proof Bar - Professional Style */}
      <section className="bg-slate-50 border-y border-slate-200 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center items-center gap-12 text-center">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-white" />
              </div>
              <div className="text-left">
                <p className="text-2xl font-bold text-slate-900">10,000+</p>
                <p className="text-sm text-slate-500">{t('home:socialProof.sitesAnalyzed')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-white" />
              </div>
              <div className="text-left">
                <p className="text-2xl font-bold text-slate-900">{t('home:socialProof.sixtySec')}</p>
                <p className="text-sm text-slate-500">{t('home:socialProof.avgAnalysis')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center">
                <Star className="h-6 w-6 text-white" />
              </div>
              <div className="text-left">
                <p className="text-2xl font-bold text-slate-900">4.9/5</p>
                <p className="text-sm text-slate-500">{t('home:socialProof.userRating')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local SEO Domination Promo */}
      <section className="py-16 px-4 bg-white" data-testid="local-seo-promo">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-8 md:p-12">
                <Badge className="bg-blue-600/20 text-blue-300 border-blue-500/30 mb-4">{t('home:localSeo.badge')}</Badge>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  {t('home:localSeo.title')}
                </h2>
                <p className="text-slate-300 mb-6">
                  {t('home:localSeo.desc')}
                </p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    {t('home:localSeo.feat1')}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    {t('home:localSeo.feat2')}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    {t('home:localSeo.feat3')}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    {t('home:localSeo.feat4')}
                  </div>
                </div>
                <Link to="/services/local-seo-domination">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6" data-testid="homepage-seo-cta">
                    {t('common:learnMore')} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <div className="hidden md:flex flex-col justify-center items-center bg-slate-800/50 p-8 space-y-4">
                <div className="text-center">
                  <div className="text-5xl font-bold text-blue-400">6</div>
                  <div className="text-sm text-slate-400 mt-1">{t('home:localSeo.stats.phases')}</div>
                </div>
                <div className="w-full h-px bg-slate-700" />
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-xl font-bold text-white">4-8</div>
                    <div className="text-xs text-slate-400">{t('home:localSeo.stats.weeks')}</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-white">200+</div>
                    <div className="text-xs text-slate-400">{t('home:localSeo.stats.keywords')}</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-white">48</div>
                    <div className="text-xs text-slate-400">{t('home:localSeo.stats.tasks')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Search Audit CTA */}
      <section className="py-16 px-4 bg-white" data-testid="ai-audit-promo">
        <div className="max-w-6xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-indigo-600 via-indigo-600 to-violet-700">
            <div className="absolute -top-24 -right-16 w-80 h-80 rounded-full bg-white/10 blur-3xl pointer-events-none" />
            <div className="relative grid md:grid-cols-2 gap-0">
              <div className="p-8 md:p-12">
                <Badge className="bg-white/15 text-white border-white/25 mb-4">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" /> New — AI Search Optimization
                </Badge>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 leading-tight">
                  Are you invisible to AI search?
                </h2>
                <p className="text-indigo-100 mb-6 leading-relaxed">
                  Your customers ask ChatGPT, Gemini and Google AI Overviews for recommendations every day.
                  Get a <strong className="text-white">free AI Search Visibility Audit</strong> and see whether you're
                  named — or whether your competitors are.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-7">
                  {['ChatGPT & Gemini visibility', 'Google AI Overviews', 'Local map-pack check', 'Clear action plan'].map((f) => (
                    <div key={f} className="flex items-center gap-2 text-sm text-indigo-50">
                      <CheckCircle2 className="w-4 h-4 text-white flex-shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>
                <Link to="/ai-search-audit">
                  <Button className="bg-white text-indigo-700 hover:bg-indigo-50 font-semibold px-6 rounded-full" data-testid="homepage-ai-audit-cta">
                    Get my free AI search audit <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <div className="hidden md:flex flex-col justify-center gap-3 p-8 bg-white/[0.06]">
                {[
                  { icon: Bot, label: 'ChatGPT' },
                  { icon: Search, label: 'Google AI Overviews' },
                  { icon: Sparkles, label: 'Gemini' },
                  { icon: Zap, label: 'Perplexity' },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-3 rounded-xl bg-white/10 backdrop-blur px-4 py-3 border border-white/10">
                    <Icon className="w-5 h-5 text-white shrink-0" />
                    <span className="text-white font-medium text-sm">{label}</span>
                    <span className="ml-auto text-xs text-indigo-100">Do you show up?</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Free Website Analyzer */}
      <section id="analyzer" className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="analyzer-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-slate-900 text-white px-4 py-1" data-testid="analyzer-badge">
              {t('home:analyzer.badge')}
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" data-testid="analyzer-title">
              {t('home:analyzer.sectionTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto" data-testid="analyzer-subtitle">
              {t('home:analyzer.sectionSubtitle')}
            </p>
          </div>


          {/* Lead Capture Modal - Teaser + Email/Phone Gate */}
          {showLeadCapture && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-300 overflow-y-auto" data-testid="lead-capture-modal">
              <Card className="w-full max-w-lg shadow-2xl border border-slate-200 animate-in slide-in-from-bottom duration-500 my-4">
                {/* Teaser Header - Show the score and key highlights */}
                <CardHeader className="bg-slate-900 text-white pb-4">
                  <div className="text-center">
                    <CardTitle className="text-xl font-bold mb-3">
                      Your Analysis is Ready
                    </CardTitle>
                    
                    {/* Score Circle */}
                    <div className="inline-flex items-center justify-center w-24 h-24 rounded-full border-4 mb-3"
                         style={{ borderColor: (pendingAnalysisResult?.overall_score || 0) >= 70 ? '#22c55e' : (pendingAnalysisResult?.overall_score || 0) >= 40 ? '#eab308' : '#ef4444' }}>
                      <span className="text-3xl font-bold">{pendingAnalysisResult?.overall_score || '?'}</span>
                      <span className="text-sm text-slate-300 ml-0.5">/100</span>
                    </div>
                    
                    <p className="text-slate-300 text-sm">
                      for <span className="font-semibold text-white">{pendingAnalysisResult?.url || websiteUrl}</span>
                    </p>
                  </div>
                </CardHeader>

                <CardContent className="pt-5 pb-4">
                  {/* Teaser Highlights */}
                  <div className="space-y-2 mb-5">
                    <div className="flex items-center gap-2 text-sm">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${(pendingAnalysisResult?.critical_issues?.length || 0) > 0 ? 'bg-red-500' : 'bg-green-500'}`}></span>
                      <span className="text-slate-700">
                        <strong>{pendingAnalysisResult?.critical_issues?.length || 0}</strong> critical issues found
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${(pendingAnalysisResult?.seo_score || 0) >= 70 ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                      <span className="text-slate-700">
                        SEO Score: <strong>{pendingAnalysisResult?.seo_score || 'N/A'}</strong>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${pendingAnalysisResult?.is_mobile_responsive ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      <span className="text-slate-700">
                        Mobile Friendly: <strong>{pendingAnalysisResult?.is_mobile_responsive ? 'Yes' : 'No'}</strong>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${pendingAnalysisResult?.has_ssl ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      <span className="text-slate-700">
                        SSL Secure: <strong>{pendingAnalysisResult?.has_ssl ? 'Yes' : 'No'}</strong>
                      </span>
                    </div>
                  </div>

                  {/* Blurred preview hint */}
                  <div className="relative mb-5 rounded-lg overflow-hidden border border-slate-200">
                    <div className="p-3 bg-slate-50 filter blur-[3px] select-none pointer-events-none">
                      <p className="text-xs text-slate-600">PageSpeed Performance: 72 | FCP: 1.2s | LCP: 2.4s</p>
                      <p className="text-xs text-slate-600">Schema Analysis: Missing structured data...</p>
                      <p className="text-xs text-slate-600">AI Visibility: Not found in ChatGPT or Gemini...</p>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                      <p className="text-sm font-semibold text-slate-800">Enter your email to unlock full report</p>
                    </div>
                  </div>

                  {/* Lead Capture Form - Name + Email required, Phone optional */}
                  <form onSubmit={handleLeadSubmit} className="space-y-3" data-testid="lead-capture-form">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        placeholder="Jane Smith"
                        value={leadForm.name}
                        onChange={(e) => setLeadForm({...leadForm, name: e.target.value})}
                        required
                        className="border-2 focus:border-blue-500"
                        data-testid="lead-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="email"
                        placeholder="you@company.com"
                        value={leadForm.email}
                        onChange={(e) => { setLeadForm({...leadForm, email: e.target.value}); if (emailVerifyState) setEmailVerifyState(null); }}
                        onBlur={(e) => { if (e.target.value) verifyLeadEmail(e.target.value); }}
                        required
                        className={`border-2 focus:border-blue-500 ${emailVerifyState?.ok === false ? 'border-red-400' : emailVerifyState?.ok ? 'border-emerald-400' : ''}`}
                        data-testid="lead-email-input"
                      />
                      {emailChecking && <div className="text-[11px] text-slate-500 mt-1" data-testid="email-checking">Checking email…</div>}
                      {emailVerifyState?.ok === false && (
                        <div className="text-[11px] mt-1 text-red-600" data-testid="lead-email-error">
                          {emailVerifyState.message}
                          {emailVerifyState.suggestion && (
                            <>
                              {' '}
                              <button type="button"
                                onClick={async () => {
                                  setLeadForm({...leadForm, email: emailVerifyState.suggestion});
                                  await verifyLeadEmail(emailVerifyState.suggestion);
                                }}
                                className="underline font-semibold text-blue-600"
                                data-testid="apply-email-suggestion"
                              >Use {emailVerifyState.suggestion}</button>
                            </>
                          )}
                        </div>
                      )}
                      {emailVerifyState?.ok && emailVerifyState.suggestion && (
                        <div className="text-[11px] mt-1 text-amber-600">
                          Did you mean{' '}
                          <button type="button" onClick={async () => {
                            setLeadForm({...leadForm, email: emailVerifyState.suggestion});
                            await verifyLeadEmail(emailVerifyState.suggestion);
                          }} className="underline font-semibold">{emailVerifyState.suggestion}</button>?
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">
                        Phone <span className="text-slate-400 text-xs">(optional)</span>
                      </label>
                      <Input
                        type="tel"
                        placeholder="+1 (555) 123-4567"
                        value={leadForm.phone}
                        onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})}
                        className="border-2 focus:border-blue-500"
                        data-testid="lead-phone-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={leadSubmitting}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-5 text-base"
                      data-testid="lead-submit-button"
                    >
                      {leadSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          {lang === 'es' ? 'Desbloqueando...' : 'Unlocking...'}
                        </>
                      ) : (
                        lang === 'es' ? 'Desbloquear Reporte Completo' : 'Unlock Full Report'
                      )}
                    </Button>
                    
                    <p className="text-center text-xs text-slate-400 mt-2">
                      {lang === 'es' ? 'Tu información es segura y nunca será compartida.' : 'Your information is secure and will never be shared.'}
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}

          {/* New Wizard Interface */}
          {useWizard && !analyzing && !analysisResult && (
            <AnalyzerWizard onComplete={handleWizardComplete} />
          )}

          {/* Legacy Form (hidden when using wizard) */}
          {!useWizard && (
          <Card className="shadow-2xl border-2 border-slate-200 bg-white" data-testid="analyzer-card">
            <CardHeader>
              <CardTitle className="text-3xl flex items-center gap-2">
                <Search className="h-8 w-8 text-blue-600" />
                Enter Your Website Details
              </CardTitle>
              <CardDescription className="text-base">
                Get personalized insights tailored to your business goals
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* URL Input */}
              <div>
                <label className="block text-sm font-semibold mb-2 text-slate-700">
                  Website URL <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <Input
                    data-testid="website-url-input"
                    type="url"
                    placeholder="https://your-website.com"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    className="flex-1 h-12 text-lg"
                  />
                </div>
              </div>

              {/* Site Type Dropdown */}
              <div>
                <label className="block text-sm font-semibold mb-2 text-slate-700">
                  What type of website is this?
                </label>
                <select
                  value={siteType}
                  onChange={(e) => setSiteType(e.target.value)}
                  className="w-full h-12 px-4 border border-slate-300 rounded-lg text-base bg-white"
                >
                  <option value="">Select type (optional)</option>
                  {siteTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Goals Checkboxes */}
              <div>
                <label className="block text-sm font-semibold mb-3 text-slate-700">
                  What are your main goals? (Select all that apply)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {goalOptions.map(goal => (
                    <label key={goal} className="flex items-center gap-2 p-3 border border-slate-200 rounded-lg hover:bg-blue-50 cursor-pointer transition">
                      <input
                        type="checkbox"
                        checked={goals.includes(goal)}
                        onChange={() => handleGoalToggle(goal)}
                        className="w-5 h-5 text-blue-600"
                      />
                      <span className="text-sm font-medium">{goal}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Competitor Comparison Section */}
              <div className="border-t border-slate-200 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                >
                  <Target className="h-4 w-4" />
                  {showAdvanced ? 'Hide' : '🔥 Compare Against a Competitor'}
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full ml-2">
                    Recommended
                  </span>
                </button>
                
                {showAdvanced && (
                  <div className="mt-4 space-y-3 animate-in slide-in-from-top-2 duration-300">
                    <p className="text-sm text-slate-600">
                      Enter a competitor's website to see how you stack up:
                    </p>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">
                        Competitor Website URL
                      </label>
                      <Input
                        placeholder="https://competitor-website.com"
                        value={leadForm.competitorUrl}
                        onChange={(e) => setLeadForm({...leadForm, competitorUrl: e.target.value})}
                        className="text-sm"
                      />
                    </div>
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      See how your site compares head-to-head!
                    </p>
                  </div>
                )}
              </div>

              {/* Dynamic Type-Specific Questions */}
              {siteType && getTypeSpecificQuestions(siteType).length > 0 && (
                <div className="space-y-4 p-6 bg-gradient-to-br from-blue-50 to-blue-50 rounded-lg border-2 border-blue-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Target className="h-6 w-6 text-blue-600" />
                    <p className="font-bold text-lg text-slate-800">
                      {siteType}-Specific Questions
                    </p>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">
                    Help us provide targeted recommendations for your industry
                  </p>
                  
                  {getTypeSpecificQuestions(siteType).map((question, idx) => (
                    <div key={idx} className="bg-white p-4 rounded-lg border border-blue-100">
                      <label className="block text-sm font-semibold mb-3 text-slate-800">
                        {idx + 1}. {question.label}
                      </label>
                      
                      {question.type === 'radio' && (
                        <div className="space-y-2">
                          {question.options.map(opt => (
                            <label key={opt} className="flex items-center gap-2 p-2 hover:bg-blue-50 rounded cursor-pointer">
                              <input
                                type="radio"
                                name={question.id}
                                value={opt}
                                checked={quizAnswers[question.id] === opt}
                                onChange={(e) => setQuizAnswers({...quizAnswers, [question.id]: e.target.value})}
                                className="w-4 h-4 text-blue-600"
                              />
                              <span className="text-sm">{opt}</span>
                            </label>
                          ))}
                        </div>
                      )}
                      
                      {question.type === 'checkbox' && (
                        <div className="space-y-2">
                          {question.options.map(opt => (
                            <label key={opt} className="flex items-center gap-2 p-2 hover:bg-blue-50 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={(quizAnswers[question.id] || []).includes(opt)}
                                onChange={(e) => {
                                  const current = quizAnswers[question.id] || [];
                                  const updated = e.target.checked 
                                    ? [...current, opt]
                                    : current.filter(item => item !== opt);
                                  setQuizAnswers({...quizAnswers, [question.id]: updated});
                                }}
                                className="w-4 h-4 text-blue-600"
                              />
                              <span className="text-sm">{opt}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  <div className="bg-green-50 border border-green-200 p-3 rounded-lg mt-4">
                    <p className="text-sm text-green-800">
                      These answers will help us create a highly customized analysis specific to {siteType} businesses.
                    </p>
                  </div>
                </div>
              )}

              {/* Analyze Button */}
              <Button 
                data-testid="analyze-btn"
                onClick={handleAnalyze} 
                disabled={analyzing || !websiteUrl}
                className="w-full h-14 text-lg font-bold bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 text-white shadow-lg"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Analyzing... {analyzingProgress.toFixed(0)}%
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-5 w-5" />
                    Get My Free Analysis Now
                  </>
                )}
              </Button>

            </CardContent>
          </Card>
          )}
          
          {/* Progress Bar & Analysis Results (shown during/after analysis) */}
          {(analyzing || analysisResult) && (
            <div className="mt-8" id="analysis-results">
              {/* AI Discovery Feed — theatrical, staged analysis screen */}
              {analyzing && (
                <div className="mb-8">
                  <AnalyzerDiscoveryFeed
                    progress={analyzingProgress}
                    message={analyzingMessage}
                    url={websiteUrl || ''}
                  />
                </div>
              )}
              
              {/* Analysis Results */}
              {analysisResult && (
                <div className="space-y-6 animate-in fade-in-50 duration-500" data-testid="analysis-results">
                  <div className="text-center p-8 bg-gradient-to-r from-blue-50 to-blue-50 rounded-lg">
                    <p className="text-sm text-slate-600 mb-2">{lang === 'es' ? 'Salud General del Sitio' : 'Overall Website Health'}</p>
                    <p className={`text-6xl font-bold ${getScoreColor(analysisResult.overall_score)}`} data-testid="overall-score">
                      {analysisResult.overall_score}<span className="text-2xl text-slate-400">/100</span>
                    </p>
                    {analysisResult.url && <p className="text-slate-500 mt-2 text-sm break-all">{analysisResult.url}</p>}
                  </div>
                  <div className="bg-white border-2 border-blue-100 rounded-lg p-8 text-center">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">{lang === 'es' ? 'Tu informe completo está listo' : 'Your full report is ready'}</h3>
                    <p className="text-slate-600 mb-6 max-w-xl mx-auto">{lang === 'es' ? 'Mira el desglose completo: rendimiento, SEO, móvil, seguridad, datos estructurados y correcciones prioritarias.' : 'See your complete breakdown — performance, SEO, mobile, security, structured data and priority fixes.'}</p>
                    <Link to="/website-analysis">
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 text-lg" data-testid="view-full-report-btn">
                        {lang === 'es' ? 'Ver informe completo' : 'View full report'}
                        <ArrowRight className="h-5 w-5 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">{t('home:testimonials.title')}</h2>
            <p className="text-slate-600">{t('home:testimonials.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Testimonial 1 */}
            <Card className="bg-slate-50 border border-slate-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-blue-500 text-blue-500" />
                  ))}
                </div>
                <p className="text-slate-700 mb-4 italic">
                  "{t('home:testimonials.t1.quote')}"
                </p>
                <div className="flex items-center gap-3">
                  <img 
                    src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=100&h=100&q=80" 
                    alt="John Smith - auto dealership owner testimonial for Web-TechServices SEO"
                    title="Web-TechServices Client - Auto Dealer Pro"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-sm">{t('home:testimonials.t1.name')}</p>
                    <p className="text-xs text-slate-500">{t('home:testimonials.t1.role')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 2 */}
            <Card className="bg-slate-50 border border-slate-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-blue-500 text-blue-500" />
                  ))}
                </div>
                <p className="text-slate-700 mb-4 italic">
                  "{t('home:testimonials.t2.quote')}"
                </p>
                <div className="flex items-center gap-3">
                  <img 
                    src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=100&h=100&q=80" 
                    alt="Maria Chen - boutique owner web design client testimonial"
                    title="Web-TechServices Client - Boutique Fashion Owner"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-sm">{t('home:testimonials.t2.name')}</p>
                    <p className="text-xs text-slate-500">{t('home:testimonials.t2.role')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 3 */}
            <Card className="bg-slate-50 border border-slate-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-blue-500 text-blue-500" />
                  ))}
                </div>
                <p className="text-slate-700 mb-4 italic">
                  "{t('home:testimonials.t3.quote')}"
                </p>
                <div className="flex items-center gap-3">
                  <img 
                    src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=100&h=100&q=80" 
                    alt="Robert Park - Arlington tech company director digital marketing testimonial"
                    title="Web-TechServices Client - Tech Solutions Director"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-sm">{t('home:testimonials.t3.name')}</p>
                    <p className="text-xs text-slate-500">{t('home:testimonials.t3.role')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Always-On SEO Intelligence promo */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50" data-testid="seo-intelligence-promo">
        <SEOIntelligenceShowcase />
      </section>

      {/* Always-On AI Marketing Team teaser */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="marketing-team-teaser-section">
        <AlwaysOnMarketingTeam compact />
      </section>

      {/* Map Dominator — Citation Scanner Promo */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="map-dominator-promo">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-slate-900 rounded-2xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-8 md:p-12">
                <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-300 px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <MapPin className="h-4 w-4" />
                  {lang === 'es' ? 'Herramienta Gratis — Sin Tarjeta de Crédito' : 'Free Tool — No Credit Card Required'}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{lang === 'es' ? 'Tu Negocio Es Invisible en Apple Maps, Waze, Alexa y 50+ Más' : 'Your Business Is Invisible on Apple Maps, Waze, Alexa & 50+ Others'}</h2>
                <p className="text-emerald-100/80 mb-6">{lang === 'es' ? (<>Todos optimizan para Google — pero <strong className="text-white">tus clientes también buscan en Apple Maps, Yelp, Waze, MapQuest, Siri, Alexa, Bing Places, Foursquare, TomTom y 45+ otros directorios</strong>. Si no estás listado, esos clientes se van con quien sí lo esté.</>) : (<>Everyone optimizes for Google — but <strong className="text-white">your customers also search on Apple Maps, Yelp, Waze, MapQuest, Siri, Alexa, Bing Places, Foursquare, TomTom, and 45+ other directories</strong>. If you're not listed, those customers go to whoever is.</>)}</p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="flex items-center gap-2 text-sm text-emerald-200">
                    <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                    {lang === 'es' ? 'Escanea 55+ directorios al instante' : 'Scan 55+ directories instantly'}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-emerald-200">
                    <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                    {lang === 'es' ? 'Ve exactamente dónde te faltan' : "See exactly where you're missing"}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-emerald-200">
                    <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                    {lang === 'es' ? 'Auto-envío a directorios' : 'Auto-submit to directories'}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-emerald-200">
                    <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                    {lang === 'es' ? 'Alertas de NAP inconsistente' : 'NAP mismatch alerts'}
                  </div>
                </div>
                <a href={lang === 'es' ? '/es/map-scan' : '/map-scan'}>
                  <button className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3 rounded-lg transition-colors shadow-lg" data-testid="homepage-map-scan-cta">
                    {lang === 'es' ? 'Ejecutar Mi Escaneo Gratis' : 'Run Your Free Scan'}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </a>
                <p className="text-emerald-300/60 text-xs mt-3">{lang === 'es' ? 'Toma 30 segundos · Sin registro' : 'Takes 30 seconds · No signup required'}</p>
              </div>
              <div className="hidden md:flex flex-col justify-center items-center bg-emerald-800/30 p-8 space-y-4">
                <div className="text-center">
                  <div className="text-5xl font-bold text-emerald-400">55+</div>
                  <div className="text-sm text-emerald-300/70 mt-1">{lang === 'es' ? 'Directorios Escaneados' : 'Directories Scanned'}</div>
                </div>
                <div className="w-full h-px bg-emerald-700/50"></div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-xl font-bold text-white">30s</div>
                    <div className="text-xs text-emerald-300/60">{lang === 'es' ? 'Resultados al Instante' : 'Instant Results'}</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-white">$49</div>
                    <div className="text-xs text-emerald-300/60">{lang === 'es' ? 'Planes desde /mes' : 'Plans from /mo'}</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-white">24/7</div>
                    <div className="text-xs text-emerald-300/60">{lang === 'es' ? 'Monitoreo Automático' : 'Auto Monitoring'}</div>
                  </div>
                </div>
                <div className="w-full h-px bg-emerald-700/50"></div>
                <div className="text-center px-4">
                  <p className="text-emerald-200 text-sm font-medium">{lang === 'es' ? '"Encontramos 38 listados faltantes. Map Dominator los arregló automáticamente."' : '"We found 38 missing listings. Map Dominator fixed them automatically."'}</p>
                  <p className="text-emerald-400/60 text-xs mt-2">{lang === 'es' ? '— Dueño de negocio local, Fort Worth TX' : '— Local business owner, Fort Worth TX'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PPC/SEM Audit Teaser */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden" data-testid="ppc-audit-teaser">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-blue-600 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm font-medium mb-4">
                <DollarSign className="h-4 w-4" />
                {t('home:ppc.badge')}
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                {t('home:ppc.titlePrefix')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">{t('home:ppc.titleAccent')}</span>
              </h2>
              <p className="text-slate-300 mb-6 text-lg">
                {t('home:ppc.desc')}
              </p>
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-400" />
                  {t('home:ppc.bullet1')}
                </div>
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-400" />
                  {t('home:ppc.bullet2')}
                </div>
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-400" />
                  {t('home:ppc.bullet3')}
                </div>
              </div>
              <Link to="/ppc-audit">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-3 text-lg" data-testid="ppc-audit-teaser-cta">
                  {t('home:ppc.cta')} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="bg-slate-800/80 backdrop-blur rounded-xl p-6 border border-slate-700">
              <div className="text-center mb-4">
                <p className="text-slate-400 text-sm uppercase tracking-wider font-medium">{t('home:ppc.avgTitle')}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-slate-300">20-40%</p>
                  <p className="text-xs text-slate-400 mt-1">{t('home:ppc.budgetWasted')}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-slate-300">3-5</p>
                  <p className="text-xs text-slate-400 mt-1">{t('home:ppc.issuesFound')}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-blue-400">$500+</p>
                  <p className="text-xs text-slate-400 mt-1">{t('home:ppc.monthlySavings')}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-blue-400">30s</p>
                  <p className="text-xs text-slate-400 mt-1">{t('home:ppc.instant')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gift Portfolio Teaser */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-slate-800 to-slate-900">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-8">
          <div className="flex-1">
            <Badge className="mb-3 bg-blue-600 text-white text-xs px-3 py-1">{t('home:giftTeaser.badge')}</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              {t('home:giftTeaser.titlePrefix')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">{t('home:giftTeaser.titleAccent')}</span>
            </h2>
            <p className="text-slate-300 mb-4">
              {t('home:giftTeaser.desc')}
            </p>
            <Link to="/gift-portfolio">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3">
                {t('home:banners.giftCta')} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="hidden md:block w-48 h-48 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center p-6 transform rotate-3">
            <div className="text-center text-white">
              <GraduationCap className="h-12 w-12 mx-auto mb-2" />
              <p className="font-bold text-sm">{t('home:giftTeaser.smallTitle')}</p>
              <p className="text-blue-100 text-xs">{t('home:giftTeaser.priceFrom')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-slate-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-600 text-white px-6 py-2 text-base font-bold">
              {lang === 'es' ? 'Estrategia Mobile-First' : 'Mobile-First Strategy'}
            </Badge>
            <h2 className="text-5xl md:text-6xl font-black mb-6 leading-tight text-white">
              {lang === 'es' ? (<>Tu Sitio Web <span className="line-through text-slate-400">NO</span> Es Suficiente en 2026</>) : (<>Your Website is <span className="line-through text-slate-400">NOT</span> Enough in 2026</>)}
            </h2>
            <p className="text-2xl md:text-3xl font-bold text-blue-300 mb-4">
              {lang === 'es' ? 'El 80% de tus clientes están en sus teléfonos ahora mismo' : '80% of Your Customers Are On Their Phones Right Now'}
            </p>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              {lang === 'es' ? 'Una App Móvil = 3X Más Engagement, 4X Mayor Conversión, Acceso Directo a tus Clientes' : 'A Mobile App = 3X More Engagement, 4X Higher Conversions, Direct Access to Your Customers'}
            </p>
          </div>

          {/* Key Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-center border border-white/20">
              <p className="text-5xl font-black text-blue-400 mb-2">3X</p>
              <p className="text-sm text-white/90">{lang === 'es' ? 'Más tiempo de uso vs sitio web' : 'More Time Spent vs Website'}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-center border border-white/20">
              <p className="text-5xl font-black text-blue-400 mb-2">4X</p>
              <p className="text-sm text-white/90">{lang === 'es' ? 'Mayor tasa de conversión' : 'Higher Conversion Rate'}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-center border border-white/20">
              <p className="text-5xl font-black text-blue-400 mb-2">70%</p>
              <p className="text-sm text-white/90">{lang === 'es' ? 'Tasa de apertura de notificaciones' : 'Push Notification Open Rate'}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-center border border-white/20">
              <p className="text-5xl font-black text-blue-400 mb-2">42%</p>
              <p className="text-sm text-white/90">{lang === 'es' ? 'Tasa de clientes recurrentes' : 'Repeat Customer Rate'}</p>
            </div>
          </div>

          {/* Quick Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card className="bg-white/10 backdrop-blur-lg border-white/20 text-white">
              <CardContent className="pt-6">
                <Smartphone className="h-10 w-10 text-blue-400 mb-4" />
                <h3 className="text-xl font-bold mb-3 text-white">{lang === 'es' ? 'Acceso con Un Solo Toque' : 'One-Tap Access'}</h3>
                <p className="text-white/80 text-sm mb-4">
                  {lang === 'es' ? 'Vive en la pantalla de inicio de tus clientes. Sin escribir URLs, sin buscar — acceso instantáneo, cada vez.' : "Live on customers' home screens. No typing URLs, no searching—instant access every time."}
                </p>
                <div className="space-y-2 text-sm text-white/90">
                  <p className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>{lang === 'es' ? 'Restaurantes: pedidos rápidos en hora pico' : 'Restaurants: Quick ordering during lunch rush'}</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>{lang === 'es' ? 'Dentistas: reservas de citas al instante' : 'Dentists: Instant appointment booking'}</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>{lang === 'es' ? 'Concesionarios: explorar inventario en cualquier momento' : 'Dealers: Browse inventory anytime'}</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-lg border-white/20 text-white">
              <CardContent className="pt-6">
                <Bell className="h-10 w-10 text-blue-400 mb-4" />
                <h3 className="text-xl font-bold mb-3 text-white">{lang === 'es' ? 'Notificaciones Push' : 'Push Notifications'}</h3>
                <p className="text-white/80 text-sm mb-4">
                  {lang === 'es' ? 'Llega a tus clientes al instante con promociones, recordatorios y novedades. Tasa de apertura del 70% vs el 20% del email.' : "Reach customers instantly with promotions, reminders, updates. 70% open rate vs email's 20%."}
                </p>
                <div className="space-y-2 text-sm text-white/90">
                  <p className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>{lang === 'es' ? 'Ofertas flash llenan horas lentas' : 'Flash sales fill slow hours'}</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>{lang === 'es' ? 'Recordatorios de citas reducen ausencias 30%' : 'Appointment reminders reduce no-shows 30%'}</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>{lang === 'es' ? 'Alertas de novedades atraen tráfico al showroom' : 'New arrivals alerts drive showroom traffic'}</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-lg border-white/20 text-white">
              <CardContent className="pt-6">
                <Target className="h-10 w-10 text-blue-400 mb-4" />
                <h3 className="text-xl font-bold mb-3 text-white">{lang === 'es' ? 'Personalización Inteligente' : 'Smart Personalization'}</h3>
                <p className="text-white/80 text-sm mb-4">
                  {lang === 'es' ? 'Recomendaciones impulsadas por IA basadas en comportamiento. Convierte compradores de una sola vez en clientes leales.' : 'AI-powered recommendations based on behavior. Turn one-time buyers into loyal customers.'}
                </p>
                <div className="space-y-2 text-sm text-white/90">
                  <p className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Loyalty rewards increase repeat visits 42%</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Custom care plans for medical practices</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Wishlists with AR vehicle previews</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Industry Examples */}
          <div className="bg-slate-800 rounded-2xl p-8 mb-12 border border-slate-700">
            <h3 className="text-3xl font-bold text-center mb-8 text-white">{lang === 'es' ? 'Perfecto Para Estos Negocios:' : 'Perfect For These Businesses:'}</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center">
                <div className="w-14 h-14 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Utensils className="h-7 w-7 text-blue-400" />
                </div>
                <p className="font-semibold text-white">{lang === 'es' ? 'Restaurantes' : 'Restaurants'}</p>
                <p className="text-xs text-white/70">{lang === 'es' ? 'Evita comisiones del 30%' : 'Bypass 30% fees'}</p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Stethoscope className="h-7 w-7 text-blue-400" />
                </div>
                <p className="font-semibold text-white">{lang === 'es' ? 'Clínicas y Salud' : 'Healthcare'}</p>
                <p className="text-xs text-white/70">{lang === 'es' ? 'Reduce ausencias' : 'Reduce no-shows'}</p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Car className="h-7 w-7 text-blue-400" />
                </div>
                <p className="font-semibold text-white">{lang === 'es' ? 'Concesionarios' : 'Car Dealers'}</p>
                <p className="text-xs text-white/70">{lang === 'es' ? 'Recorridos virtuales' : 'Virtual tours'}</p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Truck className="h-7 w-7 text-blue-400" />
                </div>
                <p className="font-semibold text-white">{lang === 'es' ? 'Distribuidores de RV' : 'RV Dealers'}</p>
                <p className="text-xs text-white/70">{lang === 'es' ? 'Muestra inventario' : 'Showcase inventory'}</p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Sparkles className="h-7 w-7 text-blue-400" />
                </div>
                <p className="font-semibold text-white">{lang === 'es' ? 'Servicios' : 'Services'}</p>
                <p className="text-xs text-white/70">{lang === 'es' ? 'Agendamiento automático' : 'Auto scheduling'}</p>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 mb-12 border border-white/20">
            <h3 className="text-3xl font-bold text-center mb-8 text-white">{lang === 'es' ? 'Solo Sitio Web vs Con App Móvil' : 'Website Only vs With Mobile App'}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-600">
                <h4 className="text-xl font-bold mb-4 text-white/70">{lang === 'es' ? 'Solo Sitio Web' : 'Website Only'}</h4>
                <ul className="space-y-3 text-sm text-white/80">
                  <li className="flex items-start gap-2">
                    <X className="h-4 w-4 text-white/50 mt-0.5 flex-shrink-0" />
                    <span>{lang === 'es' ? 'Clientes deben recordar la URL' : 'Customers must remember URL'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="h-4 w-4 text-white/50 mt-0.5 flex-shrink-0" />
                    <span>{lang === 'es' ? 'Esperas a que te visiten (pasivo)' : 'Wait for them to visit (passive)'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="h-4 w-4 text-white/50 mt-0.5 flex-shrink-0" />
                    <span>{lang === 'es' ? 'Personalización limitada' : 'Limited personalization'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="h-4 w-4 text-white/50 mt-0.5 flex-shrink-0" />
                    <span>{lang === 'es' ? 'Sin acceso offline' : 'No offline access'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="h-4 w-4 text-white/50 mt-0.5 flex-shrink-0" />
                    <span>{lang === 'es' ? 'Baja retención (29%)' : 'Low retention (29%)'}</span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-900/30 p-6 rounded-xl border-2 border-blue-500">
                <h4 className="text-xl font-bold mb-4 text-blue-300">{lang === 'es' ? 'Con App Móvil' : 'With Mobile App'}</h4>
                <ul className="space-y-3 text-sm text-white/90">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>{lang === 'es' ? 'Ícono en pantalla de inicio (siempre visible)' : 'Home screen icon (always visible)'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>{lang === 'es' ? 'Notificaciones push (proactivo)' : 'Push notifications (proactive)'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>{lang === 'es' ? 'Personalización profunda con IA' : 'Deep personalization & AI'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>{lang === 'es' ? 'Funciona sin conexión' : 'Works offline'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>{lang === 'es' ? 'Alta retención (42%)' : 'High retention (42%)'}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Link to={lang === 'es' ? '/es/aplicaciones-moviles' : '/mobile-apps'}>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xl px-10 py-7 rounded-lg shadow-xl hover:scale-105 transition-transform">
                <Smartphone className="mr-2 h-6 w-6" />
                {lang === 'es' ? 'Ver Cómo las Apps Móviles Aumentan los Ingresos' : 'See How Mobile Apps Boost Revenue'}
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
            <p className="text-sm text-slate-400 mt-4">
              {lang === 'es' ? 'Apps desde $5K-20K | ROI en 90 días | Cotizaciones personalizadas disponibles' : 'Apps start at $5K-20K | ROI in 90 days | Custom quotes available'}
            </p>
          </div>
        </div>
      </section>

      {/* Services teaser — full detail lives on /web-design-services */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50" data-testid="services-teaser-section">
        <div className="max-w-6xl mx-auto text-center">
          <Badge className="mb-4 bg-blue-600">{lang === 'es' ? 'Plataforma Impulsada por IA' : 'AI-Powered Platform'}</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            {lang === 'es' ? 'Diseño Web y Desarrollo con IA, de Extremo a Extremo' : 'End-to-End AI Web Design & Development'}
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-10">
            {lang === 'es' ? 'Desarrollo full-stack, apps móviles, modernización de sitios y optimización de rendimiento — todo por un equipo.' : 'Full-stack development, mobile apps, website modernization and performance optimization — all from one team.'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left mb-10">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100" data-testid="services-teaser-fullstack">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4"><Code className="h-6 w-6 text-blue-600" /></div>
              <h3 className="font-semibold text-lg text-slate-900 mb-1">{lang === 'es' ? 'Desarrollo Full-Stack' : 'Full-Stack Development'}</h3>
              <p className="text-slate-600 text-sm">{lang === 'es' ? 'Aplicaciones completas — frontend, backend y base de datos.' : 'Complete applications — frontend, backend and database.'}</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100" data-testid="services-teaser-modernization">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4"><Rocket className="h-6 w-6 text-yellow-600" /></div>
              <h3 className="font-semibold text-lg text-slate-900 mb-1">{lang === 'es' ? 'Modernización de Sitios' : 'Website Modernization'}</h3>
              <p className="text-slate-600 text-sm">{lang === 'es' ? 'Frameworks modernos, mejor rendimiento y mejor UX.' : 'Modern frameworks, better performance and UX.'}</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100" data-testid="services-teaser-performance">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4"><TrendingUp className="h-6 w-6 text-red-600" /></div>
              <h3 className="font-semibold text-lg text-slate-900 mb-1">{lang === 'es' ? 'Optimización de Rendimiento' : 'Performance Optimization'}</h3>
              <p className="text-slate-600 text-sm">{lang === 'es' ? 'Velocidad y SEO impulsados por IA.' : 'AI-powered speed and SEO.'}</p>
            </div>
          </div>
          <Link to="/web-design-services">
            <Button className="bg-slate-900 hover:bg-slate-800 text-white font-semibold px-8 py-3" data-testid="services-teaser-cta">
              {lang === 'es' ? 'Explorar todos los servicios' : 'Explore all services'}
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Portfolio Showcase */}
      <PortfolioShowcase />

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900" data-testid="contact-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              {lang === 'es' ? '¿Listo para Actualizar tu Sitio Web?' : 'Ready to Upgrade Your Website?'}
            </h2>
            <p className="text-xl text-slate-300">
              {lang === 'es' ? 'Obtén una consulta gratis y descubre cómo la IA puede transformar tu presencia digital' : 'Get a free consultation and see how AI can transform your digital presence'}
            </p>
          </div>

          <Card className="shadow-2xl" data-testid="contact-form-card">
            <CardHeader>
              <CardTitle className="text-2xl">{lang === 'es' ? 'Contáctanos' : 'Contact Us'}</CardTitle>
              <CardDescription>{lang === 'es' ? 'Completa el formulario y te responderemos en menos de 24 horas' : "Fill out the form below and we'll get back to you within 24 hours"}</CardDescription>
            </CardHeader>
            <CardContent>
              {contactSuccess ? (
                <div className="text-center py-8" data-testid="contact-success">
                  <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{lang === 'es' ? '¡Gracias!' : 'Thank You!'}</h3>
                  <p className="text-slate-600">{lang === 'es' ? 'Hemos recibido tu mensaje y nos pondremos en contacto pronto.' : "We've received your message and will be in touch soon."}</p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  {/* Honeypot — hidden from humans; bots fill it and get blocked server-side */}
                  <input
                    type="text"
                    name="company_url"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={contactForm.company_url}
                    onChange={(e) => setContactForm({...contactForm, company_url: e.target.value})}
                    style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Input
                        data-testid="contact-name-input"
                        placeholder={lang === 'es' ? 'Tu Nombre *' : 'Your Name *'}
                        value={contactForm.name}
                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Input
                        data-testid="contact-position-input"
                        placeholder={lang === 'es' ? 'Tu Cargo *' : 'Your Position *'}
                        value={contactForm.position}
                        onChange={(e) => setContactForm({...contactForm, position: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Input
                      data-testid="contact-company-input"
                      placeholder={lang === 'es' ? 'Nombre de la Empresa *' : 'Company Name *'}
                      value={contactForm.company}
                      onChange={(e) => setContactForm({...contactForm, company: e.target.value})}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Input
                        data-testid="contact-email-input"
                        type="email"
                        placeholder={lang === 'es' ? 'Tu Correo *' : 'Your Email *'}
                        value={contactForm.email}
                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Input
                        data-testid="contact-phone-input"
                        type="tel"
                        placeholder={lang === 'es' ? 'Tu Teléfono *' : 'Your Phone *'}
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      {lang === 'es' ? 'Método de Contacto Preferido *' : 'Preferred Contact Method *'}
                    </label>
                    <div className="flex gap-6 p-3 bg-slate-50 border-2 border-slate-200 rounded-lg">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="preferredContact"
                          value="email"
                          checked={contactForm.preferredContact === 'email'}
                          onChange={(e) => setContactForm({...contactForm, preferredContact: e.target.value})}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm font-medium">{lang === 'es' ? 'Correo' : 'Email'}</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="preferredContact"
                          value="phone"
                          checked={contactForm.preferredContact === 'phone'}
                          onChange={(e) => setContactForm({...contactForm, preferredContact: e.target.value})}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm font-medium">{lang === 'es' ? 'Teléfono' : 'Phone'}</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="preferredContact"
                          value="text"
                          checked={contactForm.preferredContact === 'text'}
                          onChange={(e) => setContactForm({...contactForm, preferredContact: e.target.value})}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm font-medium">{lang === 'es' ? 'SMS' : 'Text'}</span>
                      </label>
                    </div>
                    
                    {/* 10DLC Compliance Text - Shown when Text is selected */}
                    {contactForm.preferredContact === 'text' && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs text-slate-700 leading-relaxed">
                          {lang === 'es' ? (
                            <><strong className="text-blue-900">Aceptación de SMS:</strong> Al seleccionar mensajes de texto, aceptas recibir SMS de Web-TechServices al número proporcionado. La frecuencia de mensajes varía. Pueden aplicar tarifas de mensaje y datos. Puedes responder <strong>STOP</strong> para darte de baja o <strong>HELP</strong> para asistencia en cualquier momento. Consulta nuestros <a href="/es/terminos" className="text-blue-600 underline">Términos</a> y <a href="/es/privacidad" className="text-blue-600 underline">Política de Privacidad</a>.</>
                          ) : (
                            <><strong className="text-blue-900">SMS Opt-In:</strong> By selecting text messaging, you consent to receive SMS text messages from Web-TechServices at the phone number provided. Message frequency varies. Message and data rates may apply. You can reply <strong>STOP</strong> to opt-out or <strong>HELP</strong> for assistance at any time. View our <a href="/terms" className="text-blue-600 underline">Terms</a> and <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a>.</>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <Textarea
                      data-testid="contact-message-input"
                      placeholder="Tell us about your project *"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      required
                      rows={4}
                    />
                  </div>
                  <Button 
                    data-testid="contact-submit-btn"
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700" 
                    disabled={contactSubmitting}
                  >
                    {contactSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {lang === 'es' ? 'Enviando...' : 'Sending...'}
                      </>
                    ) : (
                      lang === 'es' ? 'Enviar Mensaje' : 'Send Message'
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Awards & Recognition Section */}
      <section className="py-12 bg-slate-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 flex items-center justify-center gap-3">
              <Trophy className="h-8 w-8 text-blue-400" />
              {lang === 'es' ? 'Agencia Web Premiada' : 'Award-Winning Web Agency'}
            </h2>
            <p className="text-slate-300">{lang === 'es' ? 'Con la confianza de más de 2,500 negocios desde el año 2000' : 'Trusted by 2,500+ businesses since 2000'}</p>
          </div>
          
          {/* Awards Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-8">
            <div className="bg-slate-700/50 rounded-lg p-4 text-center border border-slate-600">
              <Trophy className="h-8 w-8 mx-auto mb-2 text-blue-400" />
              <div className="text-white font-bold text-sm">{lang === 'es' ? 'Mejor Agencia Web 2026' : 'Top Web Agency 2026'}</div>
              <div className="text-slate-300 text-xs">{lang === 'es' ? 'Excelencia Empresarial Nacional' : 'National Business Excellence'}</div>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-4 text-center border border-slate-600">
              <Star className="h-8 w-8 mx-auto mb-2 text-blue-400" />
              <div className="text-white font-bold text-sm">{lang === 'es' ? 'Calificación 5 Estrellas' : '5-Star Rated'}</div>
              <div className="text-slate-300 text-xs">{lang === 'es' ? 'Premios Consumer Choice' : 'Consumer Choice Awards'}</div>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-4 text-center border border-slate-600">
              <Shield className="h-8 w-8 mx-auto mb-2 text-blue-400" />
              <div className="text-white font-bold text-sm">{lang === 'es' ? 'Calificación BBB A+' : 'BBB A+ Rated'}</div>
              <div className="text-slate-300 text-xs">{lang === 'es' ? 'Better Business Bureau' : 'Better Business Bureau'}</div>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-4 text-center border border-slate-600">
              <Sparkles className="h-8 w-8 mx-auto mb-2 text-blue-400" />
              <div className="text-white font-bold text-sm">{lang === 'es' ? 'Líder en Innovación IA' : 'AI Innovation Leader'}</div>
              <div className="text-slate-300 text-xs">{lang === 'es' ? 'Revista TechForward' : 'TechForward Magazine'}</div>
            </div>
          </div>
          
          {/* Featured In */}
          <div className="text-center">
            <p className="text-slate-400 text-sm mb-4 uppercase tracking-wider">{lang === 'es' ? 'Destacados En' : 'As Featured In'}</p>
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
              {['Inc. Magazine', 'Entrepreneur', 'Forbes', 'TechCrunch', 'Dallas Business Journal'].map((pub, idx) => (
                <span key={idx} className="text-slate-300 text-sm font-medium">{pub}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Rate Limit Modal */}
      {showRateLimitModal && (
        <>
          <div 
            className="fixed inset-0 bg-black/60 z-50 backdrop-blur-sm"
            onClick={() => setShowRateLimitModal(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full relative overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-slate-800 to-blue-900 p-6 text-white text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">{lang === 'es' ? 'Límite de Análisis Gratis Alcanzado' : 'Free Analysis Limit Reached'}</h3>
                <p className="text-blue-200">{lang === 'es' ? `Has usado tus ${rateLimitInfo.limit} análisis gratuitos por ${rateLimitInfo.window === 'hour' ? 'hora' : rateLimitInfo.window === 'day' ? 'día' : rateLimitInfo.window}` : `You've used your ${rateLimitInfo.limit} free analyses for this ${rateLimitInfo.window}`}</p>
              </div>
              
              {/* Content */}
              <div className="p-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">{lang === 'es' ? '¿Por qué el límite?' : 'Why the limit?'}</h4>
                  <p className="text-blue-800 text-sm">
                    {lang === 'es' ? `Cada análisis de sitio web usa recursos significativos de computación y procesamiento de IA. Ofrecemos ${rateLimitInfo.limit} análisis gratis por ${rateLimitInfo.window === 'hour' ? 'hora' : rateLimitInfo.window === 'day' ? 'día' : rateLimitInfo.window} para que todos puedan probar nuestro servicio.` : `Each website analysis uses significant computing resources and AI processing. We offer ${rateLimitInfo.limit} free analyses per ${rateLimitInfo.window} so everyone can try our service.`}
                  </p>
                </div>
                
                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-800">{lang === 'es' ? '¿Quieres acceso ilimitado?' : 'Want unlimited access?'}</h4>
                  <ul className="space-y-2 text-slate-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>{lang === 'es' ? (<><strong>Agenda una consulta gratis</strong> - Obtén un análisis completo de tu sitio con nuestro equipo experto</>) : (<><strong>Book a free consultation</strong> - Get a comprehensive analysis of your website with our expert team</>)}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>{lang === 'es' ? (<><strong>Hazte cliente</strong> - Análisis ilimitados más soporte personalizado para mejorar tus resultados</>) : (<><strong>Become a client</strong> - Unlimited analyses plus hands-on support to improve your results</>)}</span>
                    </li>
                  </ul>
                </div>
                
                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <a 
                    href={lang === 'es' ? '/es/contacto' : '/book-consultation'} 
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg text-center transition-colors"
                  >
                    {lang === 'es' ? 'Reservar Consulta Gratis' : 'Book Free Consultation'}
                  </a>
                  <a 
                    href={lang === 'es' ? '/es/contacto' : '/contact'} 
                    className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold py-3 px-6 rounded-lg text-center transition-colors"
                  >
                    {lang === 'es' ? 'Contáctanos' : 'Contact Us'}
                  </a>
                </div>
                
                <button 
                  onClick={() => setShowRateLimitModal(false)}
                  className="w-full text-slate-500 hover:text-slate-700 text-sm py-2"
                >
                  {lang === 'es' ? 'Cerrar e intentar más tarde' : 'Close and try again later'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Frequently Asked Questions — same content served to crawlers (parity) */}
      <section className="py-16 sm:py-20 bg-white" data-testid="homepage-faq-section" aria-label="Frequently Asked Questions">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Frequently Asked Questions</h2>
            <p className="text-slate-600 mt-3 text-base">Quick answers about our web design, SEO, pricing and timelines.</p>
          </div>
          <div className="space-y-3">
            {homepageFaqs.map((faq, i) => (
              <details
                key={i}
                className="group rounded-xl border border-slate-200 bg-slate-50 open:bg-white open:shadow-sm transition-colors"
                data-testid={`homepage-faq-item-${i}`}
              >
                <summary className="flex items-center justify-between cursor-pointer list-none px-5 py-4 text-left font-semibold text-slate-900">
                  <span>{faq.q}</span>
                  <ChevronDown className="h-5 w-5 text-slate-400 shrink-0 ml-3 transition-transform group-open:rotate-180" />
                </summary>
                <p className="px-5 pb-5 -mt-1 text-slate-600 leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: homepageFaqs.map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
              })),
            }),
          }}
        />
      </section>

      {/* Footer */}
    </>
  );
}


export default HomePage;
